import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Badge,
  MenuProps,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LockOutlined,
  ExperimentOutlined,
  ApiOutlined,
  CloudServerOutlined,
  CloudUploadOutlined,
  FolderOpenOutlined,
  DownloadOutlined,
  VideoCameraOutlined,
  SoundOutlined,
  AppstoreOutlined,
  CustomerServiceOutlined,
  NotificationOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [username, setUsername] = useState<string>('管理员');
  const navigate = useNavigate();
  const location = useLocation();

  // 从 localStorage 获取用户信息
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUsername(user.nickname || user.email || '管理员');
      } catch (e) {
        console.error('解析用户信息失败');
      }
    }
  }, []);

  // 侧边栏菜单配置
  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '数据看板',
      children: [
        {
          key: '/dashboard',
          label: '概览',
        },
      ],
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '用户管理',
    },
    {
      key: '/analytics',
      icon: <LineChartOutlined />,
      label: '用户行为分析',
    },
    {
      key: 'content',
      icon: <AppstoreOutlined />,
      label: '内容运营',
      children: [
        {
          key: '/content/voices',
          icon: <CustomerServiceOutlined />,
          label: '声音音色管理',
        },
        {
          key: '/content/user-voices',
          icon: <SoundOutlined />,
          label: '用户生成内容',
        },
      ],
    },
    {
      key: 'generation',
      icon: <SoundOutlined />,
      label: '生成管理',
      children: [
        {
          key: '/generation/config',
          label: '生成规模管理',
        },
      ],
    },
    {
      key: 'ai-management',
      icon: <ExperimentOutlined />,
      label: 'AI 管理',
      children: [
        {
          key: '/ai-management/config',
          label: 'QuickRouter配置',
        },
        {
          key: '/ai-management/test',
          label: 'AI测试',
        },
        {
          key: '/ai-management/costs',
          label: '费用监控',
        },
      ],
    },
    {
      key: 'core-test',
      icon: <ApiOutlined />,
      label: '核心测试管理',
      children: [
        {
          key: '/core-test/quickrouter',
          label: 'QuickRouter 测试',
        },
      ],
    },
    {
      key: 'auth',
      icon: <LockOutlined />,
      label: '权限管理',
      children: [
        {
          key: '/roles',
          label: '角色管理',
        },
        {
          key: '/permissions',
          label: '权限列表',
        },
      ],
    },
    {
      key: 'resource',
      icon: <CloudServerOutlined />,
      label: '资源管理',
      children: [
        {
          key: '/resource/list',
          icon: <FolderOpenOutlined />,
          label: '资源列表',
        },
        {
          key: '/resource/background-music',
          icon: <FolderOpenOutlined />,
          label: '背景音乐库',
        },
        {
          key: '/resource/sleep-videos',
          icon: <VideoCameraOutlined />,
          label: '助眠视频',
        },
        {
          key: '/resource/storage',
          icon: <CloudServerOutlined />,
          label: '云存储配置',
        },
      ],
    },
    {
      key: '/download/releases',
      icon: <CloudUploadOutlined />,
      label: '产品发布',
    },
    {
      key: 'data-pull',
      icon: <DownloadOutlined />,
      label: '数据拉取',
      children: [
        {
          key: '/data-pull/sleep-videos',
          label: '助眠视频',
        },
        {
          key: '/data-pull/sleep-music',
          label: '助眠音乐',
        },
      ],
    },
    {
      key: '/polaris',
      icon: <CloudUploadOutlined />,
      label: '北极星数据上报',
    },
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/system/admins',
          label: '管理员管理',
        },
        {
          key: '/settings',
          label: '系统设置',
        },
        {
          key: '/settings/login-config',
          label: '登录配置',
        },
        {
          key: '/settings/ads',
          icon: <NotificationOutlined />,
          label: '广告配置',
        },
      ],
    },
  ];

  // 用户下拉菜单
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: '个人中心',
      icon: <UserOutlined />,
    },
    {
      key: 'settings',
      label: '账户设置',
      icon: <SettingOutlined />,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (key === 'profile') {
      console.log('个人中心');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  return (
    <Layout className="main-layout">
      {/* 侧边栏 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={220}
        className="layout-sider"
      >
        <div className="logo">
          <img className="logo-icon" src="/brand-icon.svg" alt="声屿" />
          {!collapsed && <span className="logo-text">声屿管理后台</span>}
        </div>
        <div className="layout-menu-scroll">
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </div>
      </Sider>

      <Layout>
        {/* 顶部导航栏 */}
        <Header className="layout-header">
          <div className="header-left">
            {React.createElement(
              collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
              {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
              }
            )}
          </div>

          <div className="header-right">
            <Space size="large">
              {/* 通知 */}
              <Badge count={5} size="small">
                <BellOutlined className="header-icon" />
              </Badge>

              {/* 用户信息 */}
              <Dropdown
                menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
                placement="bottomRight"
              >
                <Space className="user-info">
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span className="username">{username}</span>
                </Space>
              </Dropdown>
            </Space>
          </div>
        </Header>

        {/* 内容区域 */}
        <Content className="layout-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
